/* === src/pages/auth/AuthPage.js (Final - MFA, Invisible reCAPTCHA & Corporate Domain Filter) === */
import React, { useState, useEffect, useRef } from 'react';
import {
    Card, CardContent, Typography, TextField, Button, Box, Container, Alert,
    CircularProgress, ToggleButton, ToggleButtonGroup, Tabs, Tab, 
    InputAdornment, IconButton, FormControl, InputLabel, Select, MenuItem, Grid, Divider,
    Checkbox, FormControlLabel, Link
} from '@mui/material';
import { Visibility, VisibilityOff, Security as SecurityIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const TIERS = [
    { value: 'INCUBATOR', label: 'Incubator' },
    { value: 'STARTER', label: 'Starter' },
    { value: 'GROWTH', label: 'Growth' },
    { value: 'ENTERPRISE', label: 'Enterprise' },
];

// 🚨 NEW: List of free email providers to block for Funder registrations
const FREE_EMAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 
    'aol.com', 'icloud.com', 'protonmail.com', 'zoho.com', 'yandex.com', 'mail.com'
];

const AuthPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { login } = useAuth();

    const redirectUrl = searchParams.get('redirect');
    const modeParam = searchParams.get('mode');
    const typeParam = searchParams.get('type');
    const tierParam = searchParams.get('tier');

    const isRegister = location.pathname === '/register' || modeParam === 'register';
    const [tabValue, setTabValue] = useState(isRegister ? 0 : 1);

    const [registrationType, setRegistrationType] = useState('Applicant');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    const [regData, setRegData] = useState({
        firstNames: '', surname: '', email: '', password: '', confirmPassword: '',
        companyName: '', subscriptionTier: '', role: 'Applicant'
    });
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // --- MFA STATE ---
    // step: 0 = Normal Login, 1 = Enter Code, 2 = Setup QR Code
    const [mfaState, setMfaState] = useState({ step: 0, tempToken: '', qrCode: '', secret: '', code: '' });

    // Reference for Invisible reCAPTCHA
    const recaptchaRef = useRef(null);

    const handleTabChange = (e, newValue) => {
        setTabValue(newValue);
        setError('');
        setSuccessMessage('');
        setMfaState({ step: 0, tempToken: '', qrCode: '', secret: '', code: '' }); // Reset MFA on tab switch
        if (recaptchaRef.current) recaptchaRef.current.reset();
    };

    useEffect(() => {
        if (modeParam === 'register') setTabValue(0);
        if (modeParam === 'login') setTabValue(1);

        if (typeParam === 'Funder' || tierParam) {
            setRegistrationType('Funder');
            setRegData(prev => ({ ...prev, role: 'Funder' }));
            setTabValue(0); 
            if (tierParam) {
                const matched = TIERS.find(t => t.value === tierParam || t.label === tierParam);
                if (matched) setRegData(prev => ({ ...prev, subscriptionTier: matched.value }));
            }
        }
    }, [modeParam, typeParam, tierParam]);

    const executeLoginRedirect = (user) => {
        if (redirectUrl) {
            navigate(redirectUrl);
        } else {
            switch(user.role) {
                case 'Applicant': navigate('/applicant/dashboard'); break;
                case 'Reviewer': navigate('/dashboard/reviewer'); break;
                case 'Approver': navigate('/dashboard/approver'); break;
                case 'Finance': navigate('/dashboard/finance'); break;
                case 'Admin': navigate('/admin/dashboard'); break;
                case 'SuperUser': navigate('/admin/dashboard'); break;
                default: navigate('/'); 
            }
        }
    }

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const token = await recaptchaRef.current.executeAsync();
            if (!token) {
                setError("Security verification failed. Please try again.");
                setLoading(false);
                return;
            }

            const res = await api.post('/auth/login', { ...loginData, recaptchaToken: token });
            
            // --- MFA INTERCEPTION ---
            if (res.data.requireMfaSetup) {
                // Fetch the QR code using the temporary token!
                const qrRes = await api.get('/auth/mfa/setup', { headers: { Authorization: `Bearer ${res.data.tempToken}` } });
                setMfaState({ 
                    step: 2, 
                    tempToken: res.data.tempToken, 
                    qrCode: qrRes.data.qrCodeDataUrl, 
                    secret: qrRes.data.secret, 
                    code: '' 
                });
                setSuccessMessage(res.data.message);
                setLoading(false);
                return;
            }

            if (res.data.requireMfa) {
                // Just ask for the code
                setMfaState({ step: 1, tempToken: res.data.tempToken, qrCode: '', secret: '', code: '' });
                setSuccessMessage(res.data.message);
                setLoading(false);
                return;
            }

            // No MFA required. Log them straight in!
            login(res.data.token, res.data.user, rememberMe);
            executeLoginRedirect(res.data.user);

        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
            if (recaptchaRef.current) recaptchaRef.current.reset();
        } finally {
            setLoading(false);
        }
    };

    // --- HANDLE MFA SUBMISSION ---
    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // If they are setting it up for the first time, save the secret to the DB first
            if (mfaState.step === 2) {
                await api.post('/auth/mfa/verify-setup', {
                    secret: mfaState.secret,
                    token: mfaState.code
                }, { headers: { Authorization: `Bearer ${mfaState.tempToken}` } });
            }

            // Verify the code to get the REAL access token
            const res = await api.post('/auth/login/verify-mfa', {
                tempToken: mfaState.tempToken,
                code: mfaState.code
            });

            // Boom! Access granted.
            login(res.data.token, res.data.user, rememberMe);
            executeLoginRedirect(res.data.user);

        } catch (err) {
            setError(err.response?.data?.message || 'Invalid Authenticator code.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault(); 
        
        if (regData.password !== regData.confirmPassword) {
            setError("Passwords do not match"); return;
        }

        // 🚨 NEW: Block free email domains for Funders
        if (registrationType === 'Funder' && regData.email) {
            const emailDomain = regData.email.split('@')[1]?.toLowerCase();
            if (FREE_EMAIL_DOMAINS.includes(emailDomain)) {
                setError("Funder registrations require a valid corporate domain. Free email providers (Gmail, Yahoo, etc.) are not permitted.");
                return;
            }
        }
        
        setError('');
        setSuccessMessage('');
        setLoading(true); 
        
        try {
            const token = await recaptchaRef.current.executeAsync();
            if (!token) {
                setError("Security verification failed. Please try again.");
                setLoading(false);
                return;
            }

            if (registrationType === 'Funder') {
                await api.post('/auth/register-funder', {
                    adminFirstNames: regData.firstNames,
                    adminSurname: regData.surname,
                    adminEmail: regData.email,
                    adminPassword: regData.password,
                    companyName: regData.companyName,
                    subscriptionTier: regData.subscriptionTier,
                    recaptchaToken: token
                });
                setSuccessMessage("Registration successful! Please check your email to verify your account.");
                if (recaptchaRef.current) recaptchaRef.current.reset();
                setTabValue(1); 
            } else {
                const res = await api.post('/auth/register-applicant', {
                    firstNames: regData.firstNames,
                    surname: regData.surname,
                    email: regData.email,
                    password: regData.password,
                    role: 'Applicant',
                    recaptchaToken: token 
                });

                if (res.data.token && res.data.user) {
                    login(res.data.token, res.data.user, false);
                    navigate(redirectUrl || '/applicant/dashboard');
                } else {
                    setTabValue(1);
                    setSuccessMessage("Registration successful! Please log in.");
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed');
            if (recaptchaRef.current) recaptchaRef.current.reset();
        } finally {
            setLoading(false); 
        }
    };

    return (
        <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Container maxWidth="sm">
                <Card elevation={3}>
                    {mfaState.step === 0 && (
                        <Tabs value={tabValue} onChange={handleTabChange} centered variant="fullWidth">
                            <Tab label="Create Account" />
                            <Tab label="Sign In" />
                        </Tabs>
                    )}
                    
                    <CardContent sx={{ p: 4 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                        {/* ====================================================== */}
                        {/* REGISTER VIEW */}
                        {/* ====================================================== */}
                        {tabValue === 0 && mfaState.step === 0 && (
                            <Box component="form" onSubmit={handleRegister}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                    <ToggleButtonGroup
                                        value={registrationType}
                                        exclusive
                                        onChange={(e, v) => { if(v) setRegistrationType(v); }}
                                        color="primary"
                                        size="small"
                                    >
                                        <ToggleButton value="Applicant">Applicant</ToggleButton>
                                        <ToggleButton value="Funder">Funder / Partner</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                {registrationType === 'Funder' && (
                                    <>
                                        <TextField label="Company Name" fullWidth margin="normal" 
                                            onChange={(e) => setRegData({...regData, companyName: e.target.value})} />
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Subscription Tier</InputLabel>
                                            <Select value={regData.subscriptionTier} label="Subscription Tier" 
                                                onChange={(e) => setRegData({...regData, subscriptionTier: e.target.value})}>
                                                {TIERS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </>
                                )}

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField label="First Name" fullWidth margin="normal" onChange={(e) => setRegData({...regData, firstNames: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField label="Surname" fullWidth margin="normal" onChange={(e) => setRegData({...regData, surname: e.target.value})} />
                                    </Grid>
                                </Grid>
                                
                                <TextField label="Email" fullWidth margin="normal" onChange={(e) => setRegData({...regData, email: e.target.value})} />
                                <TextField 
                                    label="Password" type={showPassword ? 'text' : 'password'} fullWidth margin="normal" 
                                    onChange={(e) => setRegData({...regData, password: e.target.value})}
                                    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>}} 
                                />

                                <TextField 
                                    label="Confirm Password" type="password" fullWidth margin="normal" 
                                    onChange={(e) => setRegData({...regData, confirmPassword: e.target.value})}
                                />

                                <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LfKhwMsAAAAAJ20byi5VnG-Il8OCsP48TcZARpz"} />

                                <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 3 }} disabled={loading}>
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Register'}
                                </Button>
                            </Box>
                        )}

                        {/* ====================================================== */}
                        {/* NORMAL LOGIN VIEW (Step 0) */}
                        {/* ====================================================== */}
                        {tabValue === 1 && mfaState.step === 0 && (
                            <Box component="form" onSubmit={handleLogin}>
                                <TextField label="Email" fullWidth margin="normal" onChange={(e) => setLoginData({...loginData, email: e.target.value})} />
                                <TextField 
                                    label="Password" type={showPassword ? 'text' : 'password'} fullWidth margin="normal" 
                                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                                    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>}} 
                                />
                                
                                {/* --- ADDED: Remember Me & Forgot Password Row --- */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 2 }}>
                                    <FormControlLabel 
                                        control={
                                            <Checkbox 
                                                checked={rememberMe} 
                                                onChange={(e) => setRememberMe(e.target.checked)} 
                                                color="primary" 
                                            />
                                        } 
                                        label={<Typography variant="body2">Remember me</Typography>} 
                                    />
                                    <Link 
                                        component={RouterLink} 
                                        to="/forgot-password" 
                                        variant="body2" 
                                        color="primary" 
                                        underline="hover"
                                    >
                                        Forgot password?
                                    </Link>
                                </Box>
                                {/* ------------------------------------------------ */}

                                <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LfKhwMsAAAAAJ20byi5VnG-Il8OCsP48TcZARpz"} />

                                <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 3 }} disabled={loading}>
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                                </Button>
                            </Box>
                        )}

                        {/* ====================================================== */}
                        {/* MFA VERIFICATION & SETUP VIEWS (Steps 1 & 2) */}
                        {/* ====================================================== */}
                        {mfaState.step > 0 && (
                            <Box component="form" onSubmit={handleMfaSubmit} sx={{ textAlign: 'center' }}>
                                <SecurityIcon color="primary" sx={{ fontSize: 60, mb: 1 }} />
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Two-Factor Authentication
                                </Typography>
                                
                                {mfaState.step === 2 && (
                                    <Box sx={{ my: 3, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Your organization requires MFA. Scan this QR code with your Authenticator App.
                                        </Typography>
                                        {mfaState.qrCode ? (
                                            <Box component="img" src={mfaState.qrCode} alt="QR Code" sx={{ width: 180, height: 180, mx: 'auto' }} />
                                        ) : (
                                            <CircularProgress />
                                        )}
                                    </Box>
                                )}

                                <Typography variant="body1" gutterBottom>
                                    Enter the 6-digit code from your Authenticator app:
                                </Typography>

                                <TextField
                                    autoFocus
                                    margin="normal"
                                    fullWidth
                                    variant="outlined"
                                    value={mfaState.code}
                                    onChange={(e) => setMfaState({ ...mfaState, code: e.target.value.replace(/\D/g, '') })}
                                    inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                                    placeholder="000000"
                                />

                                <Button 
                                    type="submit" 
                                    fullWidth 
                                    variant="contained" 
                                    size="large" 
                                    sx={{ mt: 3 }} 
                                    disabled={loading || mfaState.code.length !== 6}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
                                </Button>

                                <Button 
                                    fullWidth 
                                    variant="text" 
                                    startIcon={<ArrowBackIcon />} 
                                    sx={{ mt: 2 }} 
                                    onClick={() => setMfaState({ step: 0, tempToken: '', qrCode: '', secret: '', code: '' })}
                                >
                                    Back to Login
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default AuthPage;